'use client'

// App Router client Providers boundary (Overmind retirement, Phase D2).
//
// The Overmind <Provider> is GONE — there is no client store to establish any more.
// All request-time data is threaded as server props into the page-level providers
// (components/NamedPrices, RegionContent, RegionSurfaces, RegionOffers, RegionGeo)
// wrapped around the content in RegionPageClient; per-user state lives in Zustand
// stores (cart / session / region / offers / ui). This boundary now runs ONLY the
// GLOBAL, route-agnostic post-mount client bootstrap that every page needs:
//   - useRegionStore.getState().getCountries() — load the country-picker list.
//   - applyShippingCountryOverlay() — resolve the picker's displayed country
//     (cookie → geo-IP) THEN run the sub-region offers/shipping refinement (the
//     former Overmind applyShippingCountryOverlay pipe, reimplemented in
//     hooks/useRegionChange).
//   - prod-only service-worker registration.
//
// All of it runs in a post-mount effect (post-hydration), so none of it touches the
// SSR'd/painted HTML, and none introduces a Set-Cookie (the region cookie stays
// READ-only). getCountries / the overlay are idempotent (guarded), so co-existing
// with RegionPageClient's per-page region bootstrap is safe.

import React from 'react'
import { useRegionStore } from '../store/regionStore'
import { applyShippingCountryOverlay } from '../hooks/useRegionChange'
import enableLazyImageLoading from '../utils/enableLazyImageLoading'

// Patch React.createElement so bare <img> gets loading=lazy. Idempotent (guarded by
// an internal flag).
enableLazyImageLoading()

export default function Providers({ children }) {
  // GLOBAL, route-agnostic client bootstrap. Runs ONCE after mount
  // (post-hydration), so it only ever OVERLAYS client state (localStorage / cookie
  // / geo) on top of the already-painted SSR HTML and never affects hydration.
  React.useEffect(() => {
    // Country list + the per-user shipping-country overlay + sub-region refinement
    // (Zustand region/offers stores; the overlay was the former Overmind
    // applyShippingCountryOverlay pipe).
    useRegionStore.getState().getCountries()
    applyShippingCountryOverlay()

    // Production-only service-worker registration (dev never registers one).
    if (
      process.env.NODE_ENV === 'production' &&
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator
    ) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js').catch((error) => {
          if (process.env.NODE_ENV === 'production') {
            console.error('Service worker registration failed:', error)
          }
        })
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <>{children}</>
}
